import type { PeerSearchResult, PlaceSnapshot } from '@/types/gmb';
import {
  collectPeerRatings,
  collectPeerReviewCounts,
  computeSamplePosition,
  targetRating,
  targetReviewCount,
  type SamplePosition,
} from '@/pages/gmb-audit/utils/peerRanking';

// UI-2B｜Sample Position「本次同業樣本相對位置」（純顯示層）。
// 只使用 target + 本次有效同業樣本；缺失值排除，不視為 0。
// 明確不得宣稱 Google 排名 / 地圖排名 / SEO 排名 / 商圈排名。

function PositionTile({
  icon,
  label,
  position,
}: {
  icon: string;
  label: string;
  position: SamplePosition | null;
}) {
  return (
    <div className="rounded-2xl border border-background-300 bg-background-50 p-5 shadow-card md:p-6">
      <p className="flex items-center gap-2 text-sm font-medium text-foreground-500">
        <i className={`${icon} text-base text-primary-700`} />
        {label}
      </p>
      {position ? (
        <>
          <p className="mt-2.5 flex items-baseline gap-1.5">
            <span className="text-sm font-medium text-foreground-400">第</span>
            <span className="text-4xl font-bold leading-none text-foreground-950">{position.rank}</span>
            <span className="text-lg font-medium text-foreground-400">/ {position.total}</span>
          </p>
          <p className="mt-2 text-sm text-foreground-500">
            {position.isTied ? '與其他同業同值並列名次。' : '在本次樣本中的相對位置。'}
          </p>
        </>
      ) : (
        <p className="mt-2.5 text-lg font-bold text-foreground-500">資料不足</p>
      )}
    </div>
  );
}

export default function ReportSamplePositionSection({
  target,
  peerComparison,
}: {
  target: PlaceSnapshot;
  peerComparison?: PeerSearchResult;
}) {
  if (!peerComparison) return null;

  const peers = peerComparison.peers || [];
  const ratingPosition = computeSamplePosition(targetRating(target), collectPeerRatings(peers));
  const reviewPosition = computeSamplePosition(targetReviewCount(target), collectPeerReviewCounts(peers));

  return (
    <section className="ds-container pt-12 md:pt-16">
      <div className="mb-3 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 text-primary-700">
          <i className="ri-sort-desc text-xl" />
        </span>
        <h2 className="text-xl md:text-2xl">本次同業樣本相對位置</h2>
      </div>
      <p className="mb-6 text-base text-foreground-500">
        以本次選取的同業樣本為基準，呈現你在評分與評論數的相對位置。
      </p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <PositionTile icon="ri-star-line" label="評分相對位置" position={ratingPosition} />
        <PositionTile icon="ri-chat-3-line" label="評論數相對位置" position={reviewPosition} />
      </div>

      <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-background-300 bg-background-100 px-4 py-3.5">
        <i className="ri-information-line mt-0.5 text-base text-primary-700" />
        <p className="text-sm leading-relaxed text-foreground-600">
          此結果僅表示本次選取之同業樣本內相對位置，不是 Google
          官方搜尋排名。同值時採競爭排名（例如 1, 2, 2, 4）；資料不足的樣本不列入計算。
        </p>
      </div>
    </section>
  );
}